/**
 * Two-Factor Authentication Service (Story 1.7)
 */
import api from "./api";
import { getApiErrorMessage } from "../utils/apiError";

interface TwoFactorSetupResponse {
  qr_code: string;
  manual_key: string;
}

interface TwoFactorVerifyResponse {
  backup_codes: string[];
}

interface TwoFactorStatusResponse {
  enabled: boolean;
  backup_codes_remaining: number;
  grace_period_started: boolean;
  grace_days_remaining: number | null;
}

interface TwoFactorLoginResponse {
  access_token: string;
  token_type: string;
}

export const twoFactorService = {
  /**
   * Start 2FA setup - generates QR code and manual key
   */
  async startSetup(): Promise<{ qrCode: string; manualKey: string }> {
    try {
      const response = await api.post<TwoFactorSetupResponse>("/auth/2fa/setup");
      return {
        qrCode: response.data.qr_code,
        manualKey: response.data.manual_key,
      };
    } catch (error) {
      const message = getApiErrorMessage(error, "Failed to start 2FA setup");
      throw new Error(message);
    }
  },

  /**
   * Verify TOTP code and enable 2FA - returns backup codes
   */
  async verifyAndEnable(code: string): Promise<string[]> {
    try {
      const response = await api.post<TwoFactorVerifyResponse>("/auth/2fa/verify", {
        code,
      });
      return response.data.backup_codes;
    } catch (error) {
      const message = getApiErrorMessage(error, "Failed to verify code");
      throw new Error(message);
    }
  },

  /**
   * Disable 2FA - requires current TOTP code
   */
  async disable(code: string): Promise<void> {
    try {
      await api.post("/auth/2fa/disable", { code });
    } catch (error) {
      const message = getApiErrorMessage(error, "Failed to disable 2FA");
      throw new Error(message);
    }
  },

  /**
   * Submit 2FA code during login
   */
  async submitTwoFactorCode(
    code: string,
    sessionToken: string,
    useBackup: boolean = false
  ): Promise<string> {
    try {
      const response = await api.post<TwoFactorLoginResponse>("/auth/login/2fa", {
        code,
        session_token: sessionToken,
        use_backup: useBackup,
      });
      return response.data.access_token;
    } catch (error) {
      const message = getApiErrorMessage(error, "Failed to verify 2FA code");
      throw new Error(message);
    }
  },

  /**
   * Get current 2FA status
   */
  async getStatus(): Promise<{
    enabled: boolean;
    backupCodesRemaining: number;
    gracePeriodStarted: boolean;
    graceDaysRemaining: number | null;
  }> {
    try {
      const response = await api.get<TwoFactorStatusResponse>("/auth/2fa/status");
      return {
        enabled: response.data.enabled,
        backupCodesRemaining: response.data.backup_codes_remaining,
        gracePeriodStarted: response.data.grace_period_started,
        graceDaysRemaining: response.data.grace_days_remaining,
      };
    } catch (error) {
      const message = getApiErrorMessage(error, "Failed to get 2FA status");
      throw new Error(message);
    }
  },

  /**
   * Regenerate backup codes - requires 2FA to be enabled
   */
  async regenerateBackupCodes(): Promise<string[]> {
    try {
      const response = await api.post<{ backup_codes: string[] }>(
        "/auth/2fa/regenerate-backup-codes"
      );
      return response.data.backup_codes;
    } catch (error) {
      const message = getApiErrorMessage(error, "Failed to regenerate backup codes");
      throw new Error(message);
    }
  },
};
